/**
 * Partial-update protocol (services/aiPatch.ts).
 *
 * Instead of forcing the model to echo the FULL stack on every revise turn —
 * where one forgotten node meant an accidental deletion, and output size grew
 * with the canvas — the model returns a GeneratedPatch: only what changes.
 * Absence is a no-op.
 *
 * The patch is applied onto the current stack HERE (pure stack → stack), and
 * the patched stack then flows through the regular position-preserving merge
 * (mergeRevisedStack), so layout preservation, group re-wrap and the lowMatch
 * safety valve all keep working unchanged.
 */
import type { GeneratedPatch, GeneratedStack } from '../types';

/** Keys a patch may carry; anything else in the raw JSON is ignored. */
type PatchKey =
  | 'notes'
  | 'addNodes'
  | 'updateNodes'
  | 'removeNodeIds'
  | 'addEdges'
  | 'removeEdges'
  | 'addGroups'
  | 'updateGroups'
  | 'removeGroupIds'
  | 'addStickyNotes'
  | 'addTextNodes';

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const objectArray = (v: unknown): unknown[] | undefined =>
  Array.isArray(v) ? v.filter(isObject) : undefined;

const stringArray = (v: unknown): string[] | undefined =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.trim() !== '') : undefined;

/**
 * Structural validation of a raw model response as a patch. Returns a
 * normalized patch (only recognized, correctly-typed keys survive) or null
 * when the response isn't a patch at all — the caller then falls back to the
 * proven full-stack revise. An object with only `notes`, or all-empty
 * arrays, IS a valid no-op patch ("nothing needs changing").
 */
export function validatePatch(raw: unknown): GeneratedPatch | null {
  if (!isObject(raw)) return null;

  const patch: GeneratedPatch = {};
  let recognized = 0;

  if (typeof raw.notes === 'string') {
    patch.notes = raw.notes;
    recognized++;
  }
  const assign = <K extends PatchKey>(key: K, value: GeneratedPatch[K] | undefined) => {
    if (value !== undefined) {
      patch[key] = value;
      recognized++;
    }
  };

  assign('addNodes', objectArray(raw.addNodes) as GeneratedPatch['addNodes']);
  assign('updateNodes', objectArray(raw.updateNodes) as GeneratedPatch['updateNodes']);
  assign('removeNodeIds', stringArray(raw.removeNodeIds));
  assign('addEdges', objectArray(raw.addEdges) as GeneratedPatch['addEdges']);
  assign('removeEdges', objectArray(raw.removeEdges) as GeneratedPatch['removeEdges']);
  assign('addGroups', objectArray(raw.addGroups) as GeneratedPatch['addGroups']);
  assign('updateGroups', objectArray(raw.updateGroups) as GeneratedPatch['updateGroups']);
  assign('removeGroupIds', stringArray(raw.removeGroupIds));
  assign('addStickyNotes', objectArray(raw.addStickyNotes) as GeneratedPatch['addStickyNotes']);
  assign('addTextNodes', objectArray(raw.addTextNodes) as GeneratedPatch['addTextNodes']);

  return recognized > 0 ? patch : null;
}

export interface PatchApplyResult {
  /** The current stack with the patch applied — feed through mergeRevisedStack. */
  stack: GeneratedStack;
  /** Model's explanation of the change, when it wrote one. */
  notes?: string;
  /** Operations that were dropped (unknown ids, id collisions, bad endpoints). */
  skipped: number;
  /** Operations that were applied — 0 means a pure no-op patch. */
  appliedOps: number;
}

/**
 * Apply a validated patch onto a stack. Lenient by design: individual
 * operations referencing unknown ids are skipped (models hallucinate ids
 * under pressure), and skipping never corrupts the stack — the worst case is
 * a no-op turn, which the merge reports honestly.
 */
export function applyPatchToStack(stack: GeneratedStack, patch: GeneratedPatch): PatchApplyResult {
  const next: GeneratedStack = structuredClone(stack);
  next.nodes ??= [];
  next.edges ??= [];
  next.groups ??= [];
  next.stickyNotes ??= [];
  next.textNodes ??= [];

  let skipped = 0;
  let appliedOps = 0;

  const warn = (op: string, id: string) => {
    skipped++;
    console.warn(`aiPatch: skipped ${op} '${id}'`);
  };

  const allIds = new Set<string>();
  for (const n of next.nodes) allIds.add(n.id);
  for (const n of next.stickyNotes) allIds.add(n.id);
  for (const n of next.textNodes) allIds.add(n.id);

  // --- Removals first, so updates/adds see the post-removal world -----------
  const removedIds = new Set((patch.removeNodeIds ?? []).filter((id) => allIds.has(id)));
  for (const id of patch.removeNodeIds ?? []) {
    if (!allIds.has(id)) warn('removeNode (unknown id)', id);
  }
  if (removedIds.size > 0) {
    next.nodes = next.nodes.filter((n) => !removedIds.has(n.id));
    next.stickyNotes = next.stickyNotes.filter((n) => !removedIds.has(n.id));
    next.textNodes = next.textNodes.filter((n) => !removedIds.has(n.id));
    appliedOps++;
    // Dropping a node drops its connections for free.
    next.edges = next.edges.filter((e) => !removedIds.has(e.source) && !removedIds.has(e.target));
    for (const g of next.groups) g.nodeIds = g.nodeIds.filter((id) => !removedIds.has(id));
    for (const id of removedIds) allIds.delete(id);
  }

  // --- Updates: only fields the model explicitly set ------------------------
  const nodeById = new Map(next.nodes.map((n) => [n.id, n]));
  for (const update of patch.updateNodes ?? []) {
    const node = nodeById.get(update.id);
    if (!node) {
      warn('updateNode (unknown id)', update.id);
      continue;
    }
    const { id: _id, ...fields } = update;
    for (const [key, value] of Object.entries(fields)) {
      if (value === undefined) continue;
      if (
        key === 'label' ||
        key === 'icon' ||
        key === 'description' ||
        key === 'category' ||
        key === 'importance' ||
        key === 'groupId' ||
        key === 'layout' ||
        key === 'metadata'
      ) {
        (node as unknown as Record<string, unknown>)[key] = value;
      }
    }
    appliedOps++;
  }

  // --- Adds ------------------------------------------------------------------
  for (const node of patch.addNodes ?? []) {
    if (!node.id || allIds.has(node.id)) {
      warn('addNode (missing or colliding id)', node.id || '(none)');
      continue;
    }
    next.nodes.push(node);
    allIds.add(node.id);
    appliedOps++;
  }
  for (const note of patch.addStickyNotes ?? []) {
    if (!note.id || allIds.has(note.id)) {
      warn('addStickyNote (missing or colliding id)', note.id || '(none)');
      continue;
    }
    next.stickyNotes.push(note);
    allIds.add(note.id);
    appliedOps++;
  }
  for (const text of patch.addTextNodes ?? []) {
    if (!text.id || allIds.has(text.id)) {
      warn('addTextNode (missing or colliding id)', text.id || '(none)');
      continue;
    }
    next.textNodes.push(text);
    allIds.add(text.id);
    appliedOps++;
  }

  const edgeKey = (e: { source: string; target: string; label?: string }) =>
    `${e.source}→${e.target}::${typeof e.label === 'string' ? e.label : ''}`;
  const existingEdgeKeys = new Set(next.edges.map(edgeKey));

  for (const edge of patch.addEdges ?? []) {
    if (!edge.source || !edge.target) {
      skipped++;
      continue;
    }
    if (!allIds.has(edge.source) || !allIds.has(edge.target)) {
      warn('addEdge (endpoint does not exist)', `${edge.source}→${edge.target}`);
      continue;
    }
    if (edge.source === edge.target) {
      warn('addEdge (self-loop)', `${edge.source}→${edge.target}`);
      continue;
    }
    const key = edgeKey(edge);
    if (existingEdgeKeys.has(key)) {
      skipped++;
      continue;
    }
    next.edges.push(edge);
    existingEdgeKeys.add(key);
    appliedOps++;
  }

  for (const removal of patch.removeEdges ?? []) {
    const before = next.edges.length;
    next.edges = next.edges.filter(
      (e) =>
        !(
          e.source === removal.source &&
          e.target === removal.target &&
          (typeof removal.label !== 'string' || e.label === removal.label)
        )
    );
    if (next.edges.length !== before) appliedOps++;
  }

  // --- Groups ---------------------------------------------------------------
  const groupIds = new Set(next.groups.map((g) => g.id));
  for (const group of patch.addGroups ?? []) {
    if (!group.id || groupIds.has(group.id)) {
      warn('addGroup (missing or colliding id)', group.id || '(none)');
      continue;
    }
    group.nodeIds = (group.nodeIds ?? []).filter((id) => allIds.has(id));
    next.groups.push(group);
    groupIds.add(group.id);
    appliedOps++;
  }
  const groupById = new Map(next.groups.map((g) => [g.id, g]));
  for (const update of patch.updateGroups ?? []) {
    const group = groupById.get(update.id);
    if (!group) {
      warn('updateGroup (unknown id)', update.id);
      continue;
    }
    if (typeof update.label === 'string') group.label = update.label;
    if (typeof update.description === 'string') group.description = update.description;
    if (typeof update.color === 'string') group.color = update.color;
    if (Array.isArray(update.nodeIds)) {
      group.nodeIds = update.nodeIds.filter((id): id is string => allIds.has(id));
    }
    appliedOps++;
  }
  const removedGroups = new Set((patch.removeGroupIds ?? []).filter((id) => groupIds.has(id)));
  for (const id of patch.removeGroupIds ?? []) {
    if (!groupIds.has(id)) warn('removeGroup (unknown id)', id);
  }
  if (removedGroups.size > 0) {
    next.groups = next.groups.filter((g) => !removedGroups.has(g.id));
    // Dissolved groups leave their members on the canvas, ungrouped.
    for (const node of next.nodes) {
      if (node.groupId && removedGroups.has(node.groupId)) node.groupId = undefined;
    }
    appliedOps++;
  }

  const notes = patch.notes?.trim() || undefined;
  return { stack: next, notes, skipped, appliedOps };
}
